import { InferType } from 'yup';
import { carePlanSchema } from '@views/pages/Patient/components/CarePlan/schemas/carePlanSchema.ts';
import { useFieldArray, useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { t } from 'i18next';
import {
  useEditCarePlanMutation,
  usePatientCarePlanQuery,
  usePatientGenerateCarePlanPDF,
} from '@common/cache';
import { toast } from 'react-toastify';
import { useCallback, useContext, useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { formatApiError } from '@common/errors/error-formatting.ts';
import { PatientContext } from '@views/pages/Patient/context';
import { paths } from '@types';
import { useBlocker } from 'react-router-dom';

type IFormShape = InferType<typeof carePlanSchema>;

type ICarePlanResponse =
  paths[`/patient/{patient_id}/care-plan`]['get']['responses']['200']['content']['application/json'];

/**
 * Accepts both the form values and the values mapped from the API, which are
 * loosely typed against each other (null vs undefined vs '').
 */
type ICarePlanFormLike = {
  [K in keyof IFormShape]?: IFormShape[K] | null;
};

const initialChronicConditions = [
  {
    condition: '',
    recommendations: '',
    otherConditionText: '',
  },
];

const initialGoals = [
  {
    goal: '',
  },
];

const initialValues: IFormShape = {
  chronicConditions: initialChronicConditions,
  chronic_conditions_icd10: [''],
  fastScore: null,
  dementiaType: null,
  dementiaDiagnosis: '',
  maritalStatus: '',
  hasSteadyAndSafeLivingSituation: false,
  hasAccessToMeals: false,
  hasReliableTransportation: false,
  hasReliableUtilities: false,
  isSafeFromViolenceAndAbuse: false,
  healthRelatedSocialNeedsNote: '',
  health_care_goals: initialGoals,
  recommendation: '',
  notes: '',
  physical: '',
  medical: '',
  psychological: '',
  other: '',
  home_assessment: 'no_health_or_safety_concerns_noted',
};

const getBooleanFromString = (value?: string | boolean | null) => {
  if (typeof value === 'boolean') return value;

  return value === 'true';
};

/** The API returns `YYYY-MM-DD`, the datepicker holds a local-midnight Date. */
const parseApiDate = (value?: string | null) =>
  value ? dayjs(value.slice(0, 10)).toDate() : undefined;

const toComparableDate = (value?: Date | string | null) =>
  value && dayjs(value).isValid() ? dayjs(value).format('YYYY-MM-DD') : '';

/** Single source of truth for API -> form values, used by reset and the dirty check. */
const mapCarePlanToForm = (data: ICarePlanResponse) => ({
  chronicConditions:
    data.chronicConditions.length !== 0
      ? data.chronicConditions.map(item => ({
          condition: item.condition,
          recommendations: item.recommendations,
          otherConditionText:
            (item as { otherConditionText?: string }).otherConditionText ?? '',
        }))
      : [],
  chronic_conditions_icd10: data.chronic_conditions_icd10 ?? [],
  maritalStatus: data.healthRelatedSocialNeeds.maritalStatus,
  fastScore: data.fast_score,
  dementiaType: data.dementia_type,
  dementiaDiagnosis: data.dementia_diagnosis,
  hasSteadyAndSafeLivingSituation:
    data.healthRelatedSocialNeeds.hasSteadyAndSafeLivingSituation,
  hasAccessToMeals: data.healthRelatedSocialNeeds.hasAccessToMeals,
  hasReliableTransportation:
    data.healthRelatedSocialNeeds.hasReliableTransportation,
  hasReliableUtilities: data.healthRelatedSocialNeeds.hasReliableUtilities,
  isSafeFromViolenceAndAbuse:
    data.healthRelatedSocialNeeds.isSafeFromViolenceAndAbuse,
  healthRelatedSocialNeedsNote: data.healthRelatedSocialNeeds.notes,
  health_care_goals:
    data.health_care_goals.length !== 0
      ? data.health_care_goals.map(item => ({ goal: item }))
      : initialGoals,
  recommendation: data.recommendation,
  notes: data.notes,
  physical: data.medicalCareCoordination.physical,
  medical: data.medicalCareCoordination.medical,
  psychological: data.medicalCareCoordination.psychological,
  other: data.medicalCareCoordination.other,
  home_assessment: data.home_assessment,
  acp_date: parseApiDate(data.acp_date),
  home_assessment_date: parseApiDate(data.home_assessment_date),
});

/**
 * Canonical representation of what gets persisted: empty repeater entries are
 * dropped, empty/null/undefined collapse to '' and dates to YYYY-MM-DD, so the
 * form and the API can be compared without false positives. Applied to BOTH
 * sides of the dirty check, so key order is guaranteed to match.
 */
const getComparableValues = (values: ICarePlanFormLike) => ({
  chronicConditions: (values.chronicConditions ?? [])
    .filter(item => item?.condition || item?.recommendations)
    .map(item => ({
      condition: item?.condition || '',
      recommendations: item?.recommendations || '',
      otherConditionText: item?.otherConditionText || '',
    })),
  chronic_conditions_icd10: (values.chronic_conditions_icd10 ?? []).filter(
    Boolean,
  ),
  maritalStatus: values.maritalStatus || '',
  fastScore: values.fastScore || '',
  dementiaType: values.dementiaType || '',
  dementiaDiagnosis: values.dementiaDiagnosis || '',
  hasSteadyAndSafeLivingSituation: getBooleanFromString(
    values.hasSteadyAndSafeLivingSituation,
  ),
  hasAccessToMeals: getBooleanFromString(values.hasAccessToMeals),
  hasReliableTransportation: getBooleanFromString(
    values.hasReliableTransportation,
  ),
  hasReliableUtilities: getBooleanFromString(values.hasReliableUtilities),
  isSafeFromViolenceAndAbuse: getBooleanFromString(
    values.isSafeFromViolenceAndAbuse,
  ),
  healthRelatedSocialNeedsNote: values.healthRelatedSocialNeedsNote || '',
  health_care_goals: (values.health_care_goals ?? [])
    .map(item => item?.goal || '')
    .filter(Boolean),
  recommendation: values.recommendation || '',
  notes: values.notes || '',
  physical: values.physical || '',
  medical: values.medical || '',
  psychological: values.psychological || '',
  other: values.other || '',
  home_assessment: values.home_assessment || '',
  acp_date: toComparableDate(values.acp_date),
  // Not sent when the assessment was not completed, so it cannot be compared.
  home_assessment_date:
    values.home_assessment === 'home_assessment_concerns_not_completed'
      ? ''
      : toComparableDate(values.home_assessment_date),
});

const getIsFormDirty = (data: ICarePlanResponse, values: IFormShape) =>
  JSON.stringify(getComparableValues(values)) !==
  JSON.stringify(getComparableValues(mapCarePlanToForm(data)));

export const useCarePlan = (patient_id: string, patientFullName: string) => {
  const [isSaveChangesModalOpen, setIsSaveChangesModalOpen] = useState(false);
  const [isPageDirty, setIsPageDirty] = useState(false);
  const { mutateAsync, isPending } = useEditCarePlanMutation(patient_id);
  const { data } = usePatientCarePlanQuery(patient_id);
  const { data: pdfFile, mutateAsync: downloadPDF } =
    usePatientGenerateCarePlanPDF(patient_id);
  const {
    setIsFormValid,
    setTriggerTabChange,
    triggerTabChange,
    handleClearTrigger,
  } = useContext(PatientContext);

  const formMethods = useForm<IFormShape>({
    mode: 'onChange',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: yupResolver<IFormShape, any, any>(carePlanSchema),
    defaultValues: initialValues,
  });

  const martialStatusOptions = [
    { value: 'Single', label: t('common.martialStatusOptions.single') },
    { value: 'Married', label: t('common.martialStatusOptions.married') },
    { value: 'Divorced', label: t('common.martialStatusOptions.divorced') },
    { value: 'Widowed', label: t('common.martialStatusOptions.widowed') },
    { value: 'Separated', label: t('common.martialStatusOptions.separated') },
    {
      value: 'Domestic Partnership',
      label: t('common.martialStatusOptions.domesticPartnership'),
    },
    { value: 'Unknown', label: t('common.martialStatusOptions.unknown') },
  ];

  const fastScoreOptions = [
    {
      value: '7f - Severe Dementia, Can no longer hold head up',
      label: t('common.fastScoreOptions.7f'),
    },
    {
      value: '7e - Severe Dementia, Can no longer smile',
      label: t('common.fastScoreOptions.7e'),
    },
    {
      value: '7d - Severe Dementia, Can no longer sit up w/o assistance',
      label: t('common.fastScoreOptions.7d'),
    },
    {
      value: '7c - Severe Dementia, Can no longer walk',
      label: t('common.fastScoreOptions.7c'),
    },
    {
      value: '7b - Severe Dementia, Speaks only 1 word clearly',
      label: t('common.fastScoreOptions.7b'),
    },
    {
      value: '7a - Severe Dementia, Speaks 5-6 words during days',
      label: t('common.fastScoreOptions.7a'),
    },
    {
      value: '6e - Moderately Severe Dementia, Fecal incontinence',
      label: t('common.fastScoreOptions.6e'),
    },
    {
      value: '6d - Moderately Severe Dementia, Urinary incontinence',
      label: t('common.fastScoreOptions.6d'),
    },
    {
      value: '6c - Moderately Severe Dementia, Needs help toileting',
      label: t('common.fastScoreOptions.6c'),
    },
    {
      value: '6b - Moderately Severe Dementia, Needs help bathing',
      label: t('common.fastScoreOptions.6b'),
    },
    {
      value: '6a - Moderately Severe Dementia, Needs help putting on clothing',
      label: t('common.fastScoreOptions.6a'),
    },
    {
      value: '5 - Moderate Dementia, Needs help selecting proper attire',
      label: t('common.fastScoreOptions.5'),
    },
    {
      value: '4 - Mild Dementia, IADLs impacted',
      label: t('common.fastScoreOptions.4'),
    },
    {
      value: '3 - Mild Cognitive Impairment',
      label: t('common.fastScoreOptions.3'),
    },
    {
      value: '2 - Possible Mild Cognitive Impairment',
      label: t('common.fastScoreOptions.2'),
    },
    {
      value: '1 - No Cognitive Decline, Normal function',
      label: t('common.fastScoreOptions.1'),
    },
  ];

  const dementiaTypeOptions = [
    {
      value: "Alzheimer's disease (early stage)",
      label: t('common.dementiaTypeOptions.earlyStageAlzheimer'),
    },
    {
      value: "Alzheimer's disease (moderate stage)",
      label: t('common.dementiaTypeOptions.moderateStageAlzheimer'),
    },
    {
      value: "Alzheimer's disease (late stage)",
      label: t('common.dementiaTypeOptions.lateStageAlzheimer'),
    },
    {
      value: 'Vascular dementia',
      label: t('common.dementiaTypeOptions.vascularDementia'),
    },
    {
      value: 'Lewy Body dementia',
      label: t('common.dementiaTypeOptions.lewyBodyDementia'),
    },
    {
      value: 'Frontotemporal dementia',
      label: t('common.dementiaTypeOptions.frontotemporalDementia'),
    },
    {
      value: "Parkinson's disease dementia",
      label: t('common.dementiaTypeOptions.parkinson'),
    },
    {
      value: 'Mild cognitive impairment (MCI)',
      label: t('common.dementiaTypeOptions.mci'),
    },
    {
      value: 'Unsure/Not formally diagnosed',
      label: t('common.dementiaTypeOptions.unsureDiagnose'),
    },
  ];

  const {
    handleSubmit,
    formState: { isSubmitting, isDirty, isValid },
    reset,
    control,
    getValues,
  } = formMethods;

  const {
    remove,
    append,
    fields: chronicConditionsFields,
  } = useFieldArray({
    control,
    name: 'chronicConditions',
  });
  const {
    remove: removeGoal,
    append: appendGoal,
    fields: healthCareGoalsFields,
  } = useFieldArray({
    control,
    name: 'health_care_goals',
  });

  const handleAddChronicCondition = () => {
    append({ condition: '', recommendations: '', otherConditionText: '' });
  };

  const handleRemoveChronicCondition = (index: number) => {
    remove(index);
  };

  const handleAddHealthCareGoal = () => {
    appendGoal({ goal: '' });
  };

  const handleRemoveHealthCareGoal = (index: number) => {
    removeGoal(index);
  };

  const watchedChronicConditions = useWatch({
    control,
    name: 'chronicConditions',
  });
  const watchedHealthCareGoals = useWatch({
    control,
    name: 'health_care_goals',
  });

  const isAddChronicConditionDisabled = !!watchedChronicConditions?.find(
    item => !item.condition,
  );
  const isAddHealthGoalsDisabled = !!watchedHealthCareGoals?.find(
    item => !item.goal,
  );

  const handlePDFDownload = async ({ all_notes }: { all_notes: boolean }) => {
    toast.info(t('patient.carePlan.generatePDF.loading'));

    try {
      await downloadPDF({ all_notes });
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const submitHandler = async (values: IFormShape) => {
    const filledChronicConditions =
      values.chronicConditions?.filter(
        item => item.condition || item.recommendations,
      ) ?? [];

    try {
      await mutateAsync({
        chronicConditions: filledChronicConditions.map(item => ({
          condition: item.condition || '',
          recommendations: item.recommendations || '',
          otherConditionText: item.otherConditionText || '',
        })),
        chronic_conditions_icd10: values.chronic_conditions_icd10?.filter(
          a => !!a,
        ) as string[],
        fast_score: values.fastScore as
          | '7f - Severe Dementia, Can no longer hold head up'
          | '7e - Severe Dementia, Can no longer smile'
          | '7d - Severe Dementia, Can no longer sit up w/o assistance'
          | '7c - Severe Dementia, Can no longer walk'
          | '7b - Severe Dementia, Speaks only 1 word clearly'
          | '7a - Severe Dementia, Speaks 5-6 words during days'
          | '6e - Moderately Severe Dementia, Fecal incontinence'
          | '6d - Moderately Severe Dementia, Urinary incontinence'
          | '6c - Moderately Severe Dementia, Needs help toileting'
          | '6b - Moderately Severe Dementia, Needs help bathing'
          | '6a - Moderately Severe Dementia, Needs help putting on clothing'
          | '5 - Moderate Dementia, Needs help selecting proper attire'
          | '4 - Mild Dementia, IADLs impacted'
          | '3 - Mild Cognitive Impairment'
          | '2 - Possible Mild Cognitive Impairment'
          | '1 - No Cognitive Decline, Normal function'
          | null,
        dementia_type: values.dementiaType as
          | "Alzheimer's disease (early stage)"
          | "Alzheimer's disease (moderate stage)"
          | "Alzheimer's disease (late stage)"
          | 'Vascular dementia'
          | 'Lewy Body dementia'
          | 'Frontotemporal dementia'
          | "Parkinson's disease dementia"
          | 'Mild cognitive impairment (MCI)'
          | 'Unsure/Not formally diagnosed'
          | null,
        dementia_diagnosis: values.dementiaDiagnosis,
        healthRelatedSocialNeeds: {
          maritalStatus: values.maritalStatus as
            | 'Single'
            | 'Married'
            | 'Divorced'
            | 'Widowed'
            | 'Separated'
            | 'Domestic Partnership'
            | 'Unknown',
          hasSteadyAndSafeLivingSituation:
            !!values.hasSteadyAndSafeLivingSituation,
          hasAccessToMeals: !!values.hasAccessToMeals,
          hasReliableTransportation: !!values.hasReliableTransportation,
          hasReliableUtilities: !!values.hasReliableUtilities,
          isSafeFromViolenceAndAbuse: !!values.isSafeFromViolenceAndAbuse,
          notes: values.healthRelatedSocialNeedsNote || '',
        },
        health_care_goals:
          values.health_care_goals?.map(({ goal }) => goal)?.filter(Boolean) ??
          [],
        recommendation: values.recommendation || '',
        notes: values.notes || '',
        medicalCareCoordination: {
          physical: values.physical || '',
          medical: values.medical || '',
          psychological: values.psychological || '',
          other: values.other || '',
        },
        home_assessment: values.home_assessment as
          | 'no_health_or_safety_concerns_noted'
          | 'home_assessment_concerns_noted'
          | 'home_assessment_concerns_not_completed',
        ...(values.acp_date && {
          acp_date: dayjs(values.acp_date).format('YYYY-MM-DD'),
        }),
        ...(values.home_assessment !==
          'home_assessment_concerns_not_completed' &&
          values.home_assessment_date && {
            home_assessment_date: dayjs(values.home_assessment_date).format(
              'YYYY-MM-DD',
            ),
          }),
      });
      reset({
        ...values,
        chronicConditions: filledChronicConditions,
      });
      setIsFormValid(true);
      setIsPageDirty(false);
      toast.success('Changes saved!');
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      if (blocker.state === 'blocked') {
        blocker.proceed();
      }
    }
  };

  const onSubmit = handleSubmit(submitHandler);

  const handleCloseSaveChangesModal = () => {
    setIsSaveChangesModalOpen(false);
    handleClearTrigger();
    if (blocker.state === 'blocked') {
      blocker.reset();
    }
  };

  const handleDiscardChanges = () => {
    setIsSaveChangesModalOpen(false);
    setIsFormValid(true);
    reset();
    if (blocker.state === 'blocked') {
      blocker.proceed();
    }
  };

  const handleSaveChanges = async () => {
    // submitHandler resets the form and releases the blocker on success
    await onSubmit();
    setIsSaveChangesModalOpen(false);
  };

  useEffect(() => {
    if (data) {
      reset(mapCarePlanToForm(data));
    }
  }, [data]);

  useEffect(() => {
    if (pdfFile) {
      const url = URL.createObjectURL(pdfFile);
      const a = document.createElement('a');
      a.href = url;
      a.download = patientFullName.split(' ').join('_') || '';
      a.click();

      URL.revokeObjectURL(url);
    }
  }, [pdfFile]);

  useEffect(() => {
    if (!data) {
      setIsFormValid(isValid);
      return;
    }

    const isFormDirty = getIsFormDirty(data, getValues());
    setIsPageDirty(isFormDirty);

    if (isFormDirty && triggerTabChange) {
      setIsSaveChangesModalOpen(true);
      setTriggerTabChange(false);
      return;
    }

    setIsFormValid(!isFormDirty);
  }, [isValid, triggerTabChange, isDirty, getValues(), data]);

  const blocker = useBlocker(
    useCallback(() => {
      return isPageDirty;
    }, [isPageDirty]),
  );

  useEffect(() => {
    if (blocker.state === 'blocked') {
      setIsSaveChangesModalOpen(true);
      setTriggerTabChange(false);
    }
  }, [blocker]);

  return {
    formMethods,
    onSubmit,
    martialStatusOptions,
    fastScoreOptions,
    dementiaTypeOptions,
    chronicConditionsFields,
    handleAddChronicCondition,
    handleRemoveChronicCondition,
    isAddChronicConditionDisabled,
    healthCareGoalsFields,
    handleAddHealthCareGoal,
    handleRemoveHealthCareGoal,
    isAddHealthGoalsDisabled,
    isSubmitting,
    handlePDFDownload,
    lastUpdatedAt: data?.last_updated_at ? dayjs(data.last_updated_at) : null,
    isSaveChangesModalOpen,
    handleCloseSaveChangesModal,
    handleSaveChanges,
    handleDiscardChanges,
    isValid,
    isEditSubmitting: isPending,
    isPageDirty,
  };
};
